import {
  ChangeEventHandler,
  FC,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { faBooks } from '@fortawesome/pro-solid-svg-icons';
import {
  Button,
  createBemBlockBuilder,
  Loading,
  RadioButton,
  TextArea,
} from '@ebsco-ui/ebsco-ui';

import {
  Dropdown,
  DropdownItem,
  FeatureFlags,
  getErrorReason,
  HoldsAndCheckoutsMessage,
  Icon,
  Modal,
  ModalNotificationType,
  PlaceHoldNotification,
  RedirectToLoginButton,
} from '@app/components';
import {
  useAppContext,
  useAvailabilityContext,
  useRecordSummaryContext,
} from '@app/contexts';
import {
  QUERY_KEYS,
  useFeatureFlag,
  useHoldsAndCheckoutsPageConfig,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { FEATURE, NOTIFICATION_STATUS, ROUTE } from '@app/constants';

import { RecordSummaryModalHeader } from '../RecordSummaryModalHeader';
import { useAllowedServicePointsFetch } from './useAllowedServicePointsFetch';
import { usePlaceHoldMutation } from './usePlaceHoldMutation';

import '../Modal.scss';
import './PlaceHoldModal.scss';

interface PlaceHoldModalProps {
  isOpen: boolean;
  onModalClose: () => void;
}

const OPTIONS_GROUP_NAME = 'place-hold-option';
const COPY_TYPE_FIRST_AVAILABLE = '1';
const COPY_TYPE_SPECIFIC = '2';
const cnBemModal = createBemBlockBuilder(['modal']);
const cnBem = createBemBlockBuilder(['placeHoldModal']);

export const PlaceHoldModal: FC<PlaceHoldModalProps> = ({
  isOpen,
  onModalClose,
}) => {
  const patronCommentsRef = useRef<HTMLTextAreaElement>();
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAppContext();
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: FEATURE.consortiaLoginFlow,
  });
  const { isActive: hasLibraryNote, config: libraryNote } = useFeatureFlag({
    name: FEATURE.placeHoldLibraryNote,
  });
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();
  const [copies, setCopies] = useState<DropdownItem[]>([]);
  const [locations, setLocations] = useState<DropdownItem[]>([]);
  const [selectedCopy, setSelectedCopy] = useState<DropdownItem | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<DropdownItem | null>(
    null
  );
  const isMoreThanOneCopy = copies.length > 1;
  const initialCopyType =
    isConsortiaLogin && isMoreThanOneCopy
      ? COPY_TYPE_SPECIFIC
      : COPY_TYPE_FIRST_AVAILABLE;

  const [copyType, setCopyType] = useState(initialCopyType);
  const [status, setStatus] = useState<ModalNotificationType | null>(null);
  const { $t } = useIntl();
  const { availability, isAvailabilityLoading, getAvailability } =
    useAvailabilityContext();
  const placeHoldMutation = usePlaceHoldMutation();
  const { instance } = useRecordSummaryContext();
  const locationsQuery = useAllowedServicePointsFetch({
    id: instance.id,
    shouldFetch: isOpen && isEmpty(locations),
  });
  const locationEmptyOption = useMemo(
    () => ({
      label: $t({
        id: 'placeHoldModal.selectLocation',
        defaultMessage: 'Select location',
      }),
    }),
    [$t]
  );
  const titleMessage = selectConfiguredOption(
    $t(sharedMessages.requestItem),
    $t(sharedMessages.placeHold)
  );

  const resetRelayOnModalValues = () => {
    setCopyType(initialCopyType);
    setSelectedCopy(null);
    setSelectedLocation(locationEmptyOption);
    setStatus(null);
  };

  const handleCopyTypeChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => {
    setCopyType(value);
    setSelectedCopy(value === COPY_TYPE_SPECIFIC ? copies[0] : null);
  };

  useEffect(() => {
    const recordAvailability = availability[instance.id];

    if (recordAvailability) {
      const recordCopies = recordAvailability.availabilityData.map(
        holdingAvailability => ({
          label: holdingAvailability?.volume
            ? `${holdingAvailability.callNumber} ${holdingAvailability.volume}`
            : holdingAvailability.callNumber,
          id: holdingAvailability.id,
        })
      ) as DropdownItem[];

      setCopies(recordCopies);
    }
  }, [instance.id, availability]);

  useEffect(() => {
    if (locationsQuery.data) {
      setLocations([locationEmptyOption, ...locationsQuery.data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsQuery.data]);

  useEffect(() => {
    if (
      placeHoldMutation.status === NOTIFICATION_STATUS.success ||
      placeHoldMutation.status === NOTIFICATION_STATUS.error
    ) {
      setStatus(placeHoldMutation.status);
    }

    if (placeHoldMutation.status === NOTIFICATION_STATUS.success) {
      getAvailability([instance.id]);
      queryClient.refetchQueries([QUERY_KEYS.userItemStatuses], {
        type: 'active',
      });
    }
  }, [queryClient, getAvailability, placeHoldMutation.status, instance.id]);

  useEffect(() => {
    setCopyType(initialCopyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copies]);

  const getModalBody = () => {
    if (!isLoggedIn) {
      return (
        <HoldsAndCheckoutsMessage
          requestsMessage={$t({
            id: 'placeHoldModal.signInOrCreateAccountToRequest',
            defaultMessage: 'To request an item, sign in.',
          })}
          holdsMessage={$t({
            id: 'placeHoldModal.signInOrCreateAccount',
            defaultMessage: 'To place a hold, sign in.',
          })}
        />
      );
    }

    return status ? (
      <PlaceHoldNotification
        type={status}
        queuePosition={placeHoldMutation?.data?.queuePosition}
        errorMessage={getErrorReason(placeHoldMutation)}
      />
    ) : (
      <form>
        {hasLibraryNote && libraryNote?.note && (
          <div className={cnBem('__libraryNote')} data-auto="library-note">
            {$t(
              {
                id: 'placeHoldModal.note',
                defaultMessage: '<b>Note:</b> {note}',
              },
              {
                b: chunks => <span>{chunks}</span>,
                note: libraryNote.note,
              }
            )}
          </div>
        )}
        <div className={cnBem('__formContainer')}>
          {isMoreThanOneCopy && (
            <>
              <FeatureFlags
                flagsToMatch={[{ name: FEATURE.consortiaLoginFlow }]}
                renderOff={() => (
                  <fieldset>
                    <legend>
                      <FormattedMessage
                        id="placeHoldModal.iWantToGet"
                        defaultMessage="I want to get:"
                      />
                    </legend>
                    <RadioButton
                      label={$t({
                        id: 'placeHoldModal.firstAvailableCopy',
                        defaultMessage: 'First available copy',
                      })}
                      name={OPTIONS_GROUP_NAME}
                      value={COPY_TYPE_FIRST_AVAILABLE}
                      checked={COPY_TYPE_FIRST_AVAILABLE === copyType}
                      onChange={handleCopyTypeChange}
                    />
                    <RadioButton
                      label={$t({
                        id: 'placeHoldModal.specificCopy',
                        defaultMessage: 'A specific copy (volume)',
                      })}
                      name={OPTIONS_GROUP_NAME}
                      value={COPY_TYPE_SPECIFIC}
                      checked={COPY_TYPE_SPECIFIC === copyType}
                      onChange={handleCopyTypeChange}
                    />
                  </fieldset>
                )}
              ></FeatureFlags>
              {copyType === COPY_TYPE_SPECIFIC && (
                <Dropdown
                  label={$t({
                    id: 'placeHoldModal.chooseCopy',
                    defaultMessage: 'Choose a copy',
                  })}
                  id="place-hold-choose-copy"
                  selectedItem={selectedCopy as DropdownItem}
                  items={copies}
                  styleType="standard"
                  onSelectedItemChange={({ selectedItem }) => {
                    setSelectedCopy(selectedItem ?? null);
                  }}
                />
              )}
            </>
          )}
          {!locationsQuery.isError && (
            <Dropdown
              label={$t({
                id: 'placeHoldModal.choosePickupLocation',
                defaultMessage: 'Choose a pickup location',
              })}
              items={locations}
              id="place-hold-choose-location"
              styleType="standard"
              selectedItem={
                (selectedLocation as DropdownItem) ?? locationEmptyOption
              }
              isLoading={locationsQuery.isLoading}
              onSelectedItemChange={({ selectedItem }) => {
                setSelectedLocation(selectedItem ?? null);
              }}
            />
          )}
          <FeatureFlags flagsToMatch={[{ name: FEATURE.placeHoldNotes }]}>
            <TextArea
              id="patron-comments"
              ref={patronCommentsRef as Ref<HTMLTextAreaElement>}
              label={$t({
                id: 'placeHoldModal.notes',
                defaultMessage: 'Notes (optional)',
              })}
            />
          </FeatureFlags>
        </div>
      </form>
    );
  };

  const getModalFooter = () => {
    if (!isLoggedIn) {
      return (
        <RedirectToLoginButton
          redirectBackRoute={`${generatePath(ROUTE.instanceDetails, {
            id: instance.id,
          })}?isPlaceHold=true`}
        />
      );
    }

    return (
      <>
        <Button styleType="text" onClick={onModalClose}>
          <FormattedMessage
            {...(status ? sharedMessages.close : sharedMessages.cancel)}
          />
        </Button>
        {!status && (
          <Button
            styleType="default"
            disabled={!selectedLocation?.id}
            onClick={() => {
              if (selectedLocation?.id) {
                const item =
                  copyType === COPY_TYPE_SPECIFIC
                    ? selectedCopy ?? copies[0]
                    : null;

                placeHoldMutation.mutate({
                  instanceId: instance.id,
                  pickupLocationId: selectedLocation.id,
                  itemId: item?.id,
                  patronComments: patronCommentsRef.current?.value,
                });
              }
            }}
          >
            <Icon icon={faBooks} />
            {titleMessage}
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={titleMessage}
      onClose={onModalClose}
      onAfterClose={resetRelayOnModalValues}
    >
      <div
        className={cn({
          [cnBemModal('__disabledScroll')]: placeHoldMutation.isLoading,
          [cnBemModal('--is-logged-out')]: !isLoggedIn,
        })}
      >
        <RecordSummaryModalHeader
          headerTitle={titleMessage}
          onModalClose={onModalClose}
        />
        <Modal.Body>{getModalBody()}</Modal.Body>
        <Modal.Footer>{getModalFooter()}</Modal.Footer>
        {(placeHoldMutation.isLoading ||
          (isAvailabilityLoading && status === null)) && (
          <Loading
            loadingMessage={
              isAvailabilityLoading
                ? $t(sharedMessages.loadingMessage)
                : selectConfiguredOption(
                    $t({
                      id: 'placeHoldModal.requestingItem',
                      defaultMessage: 'Requesting item...',
                    }),
                    $t({
                      id: 'placeHoldModal.placingHold',
                      defaultMessage: 'Placing hold...',
                    })
                  )
            }
          />
        )}
      </div>
    </Modal>
  );
};
